<script>
  import { LayerCake, Svg } from "layercake";
  import { Portal } from "bits-ui";
  import { AxisX } from "@urbaninstitute/dataviz-components";
  import { ColorLegend } from "@urbaninstitute/dataviz-components/maps";
  import BeeswarmForce from "./ChartParts/BeeswarmForce.svelte";
  import ArrowLong from "./ChartParts/ArrowLong.svelte";
  import TooltipWrapper from "./ChartParts/TooltipWrapper.svelte";
  import { getContext } from "svelte";
  import { useData } from "$utils/data.svelte.js";
  import { getTier } from "$utils/tier.svelte.js";
  import { LEFT_X_AXIS_LABEL } from "$utils/constants.js";
  import { scaleOrdinal } from "d3";
  import { urbanColors } from "@urbaninstitute/dataviz-components/utils";

  let toolData = useData();

  let seriesColors = [urbanColors.blue, urbanColors.yellow, urbanColors.green];
  let xDomain = $derived(
    toolData.activeSegregationMetric == "NormalizedExpWhiteBlack" ? [0, 0.72] : [0, 0.51]
  );

  let tooltipTop = $state(null);
  let tooltipBottom = $state(null);

  let r = 7;
  let dotStroke = "var(--neutral-darker, #696969)";

  // let dotStrokeWidth = .5 - Challenge here. This udates the dots but not the legend.
  let dotStrokeWidth = 1;

  let viewportWidth = $state(0);

  // App-owned tooltip layer (from Tool.svelte via context): a div outside the zoom:0.8 notebook
  // but inside this component's DOM/shadow root. Portaling here fixes both the zoom drift and the
  // CMS-embed "tooltip doesn't show" issue (no fragile document/.theme lookup that can return null).
  const getTooltipLayer = getContext("tooltipLayer");
  let portalTarget = $derived(getTooltipLayer?.());
</script>

<svelte:window bind:innerWidth={viewportWidth} />

<div class="chart-legend-container">
  {#if toolData.rightBeesSortedData}
    <div class="chart-container top-chart">
      <p class="axis-label">Districts with these policies</p>
      <div
        class="legend"
        class:two-row={toolData.activeRightChartGrouping === "region"}
        style:--dot-stroke={dotStroke}
        style:--dot-stroke-width={`${dotStrokeWidth}px`}
      >
        <ColorLegend
          scale={toolData.activeRightChartGrouping
            ? scaleOrdinal().domain(toolData.activeGroupingNames).range(seriesColors)
            : scaleOrdinal().domain(["All districts"]).range(seriesColors)}
          swatch
          swatchCircle
          swatchSize={r * 2}
          swatchAlign="flex-start"
        />
      </div>
      <div class="top-bees">
        <LayerCake
          padding={{ bottom: 15, right: 15 }}
          x={toolData.activeSegregationMetric}
          z={toolData.activeRightChartGrouping}
          zScale={scaleOrdinal()}
          zDomain={toolData.activeGroupingValues}
          zRange={seriesColors}
          {xDomain}
          data={toolData.rightBeesSortedData.match}
          custom={{
            seriesColors: seriesColors,
            groupType: toolData.activeRightChartGrouping,
            selectedID: toolData.activeDistrictID,
            onClick: (d) => (toolData.activeDistrictID = d.DistrictID)
          }}
        >
          {#if tooltipTop && portalTarget}
            <Portal to={portalTarget}
              ><TooltipWrapper
                x={tooltipTop.x}
                y={tooltipTop.y}
                state={tooltipTop.data.State}
                name={tooltipTop.data.DistrictName}
                active_seg_value={tooltipTop.data[toolData.activeSegregationMetric]}
                active_seg_label={toolData.activeSegregationMetricLabel}
                extra_label={toolData.activeRightChartGroupingLabel}
                extra_value={toolData.getGroupingValueLabelFromValue(
                  tooltipTop.data[toolData.activeRightChartGrouping]
                )}
              /></Portal
            >
          {/if}
          <Svg>
            <BeeswarmForce
              {r}
              stroke={dotStroke}
              strokeWidth={dotStrokeWidth}
              showMidline={true}
              midlineStroke={"#d9d9d9"}
              midlineStrokeWidth={1}
              xStrength={0.95}
              yStrength={0.075}
              bind:tooltip={tooltipTop}
            />
          </Svg>
        </LayerCake>
      </div>
    </div>
    <div class="chart-container bottom-chart">
      <p class="axis-label">Other districts</p>
      <LayerCake
        padding={{ bottom: 15, right: 15 }}
        x={toolData.activeSegregationMetric}
        z={toolData.activeRightChartGrouping}
        zScale={scaleOrdinal()}
        zDomain={toolData.activeGroupingValues}
        zRange={seriesColors}
        {xDomain}
        data={toolData.rightBeesSortedData.nonMatch}
        custom={{
          seriesColors: seriesColors,
          groupType: toolData.activeRightChartGrouping,
          selectedID: toolData.activeDistrictID,
          onClick: (d) => (toolData.activeDistrictID = d.DistrictID)
        }}
        >{#if tooltipBottom && portalTarget}
          <Portal to={portalTarget}
            ><TooltipWrapper
              x={tooltipBottom.x}
              y={tooltipBottom.y}
              state={tooltipBottom.data.State}
              name={tooltipBottom.data.DistrictName}
              active_seg_value={tooltipBottom.data[toolData.activeSegregationMetric]}
              active_seg_label={toolData.activeSegregationMetricLabel}
              extra_label={toolData.activeRightChartGroupingLabel}
              extra_value={toolData.getGroupingValueLabelFromValue(
                tooltipBottom.data[toolData.activeRightChartGrouping]
              )}
            /></Portal
          >
        {/if}
        <Svg>
          <BeeswarmForce
            {r}
            stroke={dotStroke}
            strokeWidth={dotStrokeWidth}
            showMidline={true}
            midlineStroke={"#d9d9d9"}
            midlineStrokeWidth={1}
            xStrength={0.95}
            yStrength={0.075}
            bind:tooltip={tooltipBottom}
          />
          <AxisX tickMarks={false} ticks={getTier(viewportWidth) === "mobile" ? 4 : 11} />
          <!-- <AxisY gridlines={true} /> -->
        </Svg>
      </LayerCake>
      <p class="x-axis-label-right">{LEFT_X_AXIS_LABEL}<ArrowLong /></p>
    </div>
  {/if}
</div>

<style>
  .chart-legend-container {
    background-color: rgba(255, 255, 255, 0.72);
    min-height: 300px;
    height: auto;
    padding: 5px 15px 20px 15px;
  }
  .chart-container.top-chart {
    height: auto;
    padding: 20px 10px 0 15px;
  }
  .top-bees {
    height: 80px;
  }
  .chart-container.bottom-chart {
    height: 180px;
    padding-top: 0;
    position: relative;
  }
  p.x-axis-label-right {
    position: absolute;
    right: 12px;
    bottom: 24px;
    margin: 0;
    font-size: 14px !important;
    line-height: normal;
    font-weight: var(--font-weight-normal);
    text-transform: none;
    letter-spacing: normal;
    font-family: inherit;
    background-color: transparent;
    padding: 0 1px;
    white-space: nowrap;
    pointer-events: none;
  }
  .legend {
    height: 22px;
    margin-top: 15px;
    margin-bottom: 10px;
  }
  .legend :global(.legend-wrapper) {
    width: 100%;
    display: flex;
    justify-content: flex-start;
  }
  .legend :global(.swatch-color) {
    outline: var(--dot-stroke-width) solid var(--dot-stroke);
  }
  .chart-container {
    display: column;
    grid-template-columns: 70px 1fr;
    gap: 10px;
    padding: 2px 10px 15px 15px;
    p.axis-label {
      margin: 0;
      font-size: 14px;
      line-height: 125%;
      font-weight: var(--font-weight-bold);
      letter-spacing: 1.4px;
      font-family: var(--font-family-sans-alt);
      text-transform: uppercase;
      align-self: center;
    }
  }

  @media (max-width: 1159px) {
    .legend.two-row {
      height: 50px;
    }
  }

  @media (max-width: 799px) {
    .top-bees {
      height: 140px;
    }

    .chart-container.bottom-chart {
      height: 210px;
    }

    /* Shift the legend left to reclaim horizontal room, and keep each region label on a
       single line so the trailing "US" (e.g. "Central or Southern US") doesn't wrap. */
    .legend {
      margin-left: -12px;
    }
    .legend :global(.swatch-label) {
      white-space: nowrap;
    }
  }
</style>
